import React, { useEffect, useReducer, useRef } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import { Box, Button } from "@mui/material";
import { useParams } from "react-router-dom";
import useBackend from "../../hooks/use_backend";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { fetchBufferedMetering } from "scripts/api_v2/commands_client";
import { fetchSiteData } from "../../scripts/api_v2/site_client";
import { useQuery } from "@tanstack/react-query";
import Loader from "../general/loader";
import { LIVE_DATA_WARNINGS } from "../../pages/elion_site/tabs/commissioning_tab/components/enum/live_data_warnings";
import { DeviceClass } from "model/device";
import {
  COMMISSIONING_WIZARD_REDUCER,
  commissioningWizardInitialState,
  commissioningWizardReducer,
} from "../../pages/elion_site/tabs/commissioning_tab/components/hooks/commissioning_wizard_reducer";

ChartJS.register(
  TimeScale,
  LinearScale,
  CategoryScale,
  LineController,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

export const getDeviceLiveChartOptions = () => ({
  animation: false,
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index",
    intersect: false,
  },
  plugins: {
    legend: {
      position: "bottom",
      display: true,
    },
  },
  scales: {
    x: {
      type: "time",
      time: {
        unit: "minute",
        displayFormats: {
          minute: "HH:mm",
        },
      },
    },
    y: {},
  },
});

const DeviceMeteringChart = ({ data, options, sx }) => {
  return (
    <Box boxSizing="border-box" display="flex" height="250px" width="100%">
      <Line data={data} options={options} width="100%" />
    </Box>
  );
};

export function DeviceMeteringLive({ device, isGrid = false, dispatchCommissioningStateChanges = null }) {
  const params = useParams();
  const backend = useBackend();

  const [paused, setPaused] = React.useState(false);
  const pausedRef = React.useRef(false);
  const since = React.useRef(0);
  const labelsRef = React.useRef([]);
  const dataRef = React.useRef({
    l1: [],
    l2: [],
    l3: [],
    reactive_l1: [],
    reactive_l2: [],
    reactive_l3: [],
    flex: [],
    setpoint: [],
    production: [],
    production_setpoint: [],
    curtailment: [],
    // "production_target": [],
    // "reactive_flex": [],
    // 'reactive_flex_target': [],
    // "reactive_flex_setpoint": []
  });

  const [labels, setLabels] = React.useState(labelsRef.current);
  const numberOfPoints = 180 * 2; // only show three minutes of data
  const [data, setData] = React.useState(dataRef.current);

  const {
    data: newDataPoint,
    isFetching,
    error: dataFetchingError,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.DEVICE_METERING_LIVE, backend.fetch, params.site_id, device.device_id],
    queryFn: async () => {
      try {
        return await fetchBufferedMetering(backend.fetch, params.site_id, device.device_id, since.current);
      } catch (error) {
        console.error("Error in query function:", error);
        throw error; // Rethrow to let react-query handle it
      }
    },
    refetchInterval: 5000,
    enabled: !!params.site_id && !pausedRef.current,
  });
  useEffect(() => {
    if (dispatchCommissioningStateChanges != null && !isFetching && newDataPoint != null) {
      dispatchCommissioningStateChanges({
        type: COMMISSIONING_WIZARD_REDUCER.METERING_LIVE_DATA_FETCHED,
        payload: true,
      });
    } else if (dispatchCommissioningStateChanges != null && newDataPoint === null) {
      dispatchCommissioningStateChanges({
        type: COMMISSIONING_WIZARD_REDUCER.METERING_DATA_FETCHED,
        payload: false,
      });
    }
  }, [isFetching, newDataPoint]);

  useEffect(() => {
    // TODO: DO WE NEED TO CHECK ON newDataPoint === undefined?
    if (dispatchCommissioningStateChanges !== null && ((!isFetching && newDataPoint === null) || dataFetchingError)) {
      dispatchCommissioningStateChanges({
        type: COMMISSIONING_WIZARD_REDUCER.STEP_WARNING_FOUND,
        payload: LIVE_DATA_WARNINGS.DEVICE_LIVE_METERING_COMPONENT,
      });
    }
  }, [dataFetchingError, isFetching, newDataPoint]);

  function mapDeviceField(deviceClass, isGrid, field) {
    const mappables = ["l1", "l2", "l3"];
    if (!isMeter(deviceClass) && isGrid && mappables.includes(field)) {
      return "grid_" + field;
    }
    return field;
  }

  function isMeter(deviceClass) {
    return DeviceClass.METER === deviceClass;
  }

  function isBattery(deviceClass) {
    return DeviceClass.BATTERY === deviceClass;
  }

  useEffect(() => {
    // There is no onSuccess for useQuery...
    if (newDataPoint != null && newDataPoint != undefined) {
      try {
        const timestamps = newDataPoint.timestamp;
        const new_ts = timestamps.slice(-1)[0];
        since.current = new_ts;
        labelsRef.current = [
          ...labelsRef.current,
          ...timestamps.map(function (e) {
            return new Date(1000 * e);
          }),
        ].slice(-numberOfPoints);
        Object.entries(dataRef.current).forEach(([key, values]) => {
          const devKey = mapDeviceField(device.device_class, isGrid, key);
          if (devKey in newDataPoint) {
            dataRef.current[key] = [...values, ...newDataPoint[devKey]].slice(-numberOfPoints);
          }
        });
      } catch (error) {
        console.error("failure during device live metering", error);
      }
      setLabels(labelsRef.current);
      setData(dataRef.current);
    }
  }, [newDataPoint]);
  return newDataPoint == null && isFetching ? (
    <Loader fill={true} />
  ) : !dataFetchingError ? (
    <Box ml={1} display="flex" flexDirection="column" height="100%" width="100%">
      <Box display="flex" flexDirection="column" width="100%" justifyContent="space-between">
        <LiveGridChart device={device} labels={labels} data={data} />
        {/* <LiveReactiveChart device={device} labels={labels} data={data}/> */}
        {!isGrid && isBattery(device.device_class) && <LiveFlexChart device={device} labels={labels} data={data} />}
        {!isGrid && !isMeter(device.device_class) && (
          <LiveCurtailmentChart device={device} labels={labels} data={data} />
        )}
      </Box>
      <Button
        sx={{ m: 1, width: "200px", alignSelf: "center" }}
        onClick={() => {
          setPaused(!paused);
          pausedRef.current = !pausedRef.current;
        }}
      >
        {paused ? "Unpause" : "Pause"}
      </Button>
    </Box>
  ) : (
    <div>Error loading data</div>
  );
}

const LiveFlexChart = ({ device, labels, data }) => {
  const params = useParams();
  const backend = useBackend();

  const options = getDeviceLiveChartOptions();
  options.scales.y.suggestedMin = -device?.charge_rate * 1.1;
  options.scales.y.suggestedMax = device?.charge_rate * 1.1;

  const flexData = {
    labels: labels,
    datasets: [
      {
        fill: false,
        label: "Flex",
        data: data.flex?.map((el) => (el ? Math.round(el) : el)),
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(255, 153, 0, 1)",
        backgroundColor: "rgba(255, 153, 0, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "Flex setpoint",
        data: data.flex_setpoint?.map((el) => (el ? Math.round(el) : el)),
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(100, 100, 100, 0.5)",
        backgroundColor: "rgba(100, 100, 100, 0.5)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "Flex target",
        data: data.flex_target?.map((el) => (el ? Math.round(el) : el)),
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(0, 0, 0, 0.8)",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        pointStyle: false,
        type: "line",
      },
    ],
  };

  return (
    <Box height="250px">
      <Line data={flexData} width={"100%"} options={options} />
    </Box>
  );
};

const LiveGridChart = ({ device, labels, data }) => {
  const params = useParams();
  const backend = useBackend();

  let {
    data: siteData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  const options = getDeviceLiveChartOptions();
  //options.scales.y.suggestedMin = -siteData?.config?.max_grid_inject_sp * 1.1;
  //options.scales.y.suggestedMax = siteData?.config?.max_grid_offtake_sp * 1.1;

  options.scales.y = {
    beginAtZero: false,
    grace: "20%",
  };

  options.scales.x = {
    type: "time",
    time: {
      unit: "minute",
      displayFormats: {
        minute: "HH:mm",
      },
    },
  };

  const gridData = {
    labels: labels,
    datasets: [
      {
        fill: false,
        label: "L1 Active",
        data: data.l1?.map((el) => (el ? Math.round(el) : el)),
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(102, 0, 205, 1)",
        backgroundColor: "rgba(102, 0, 205, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "L2 Active",
        data: data.l2?.map((el) => (el ? Math.round(el) : el)),
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(0, 102, 205, 1)",
        backgroundColor: "rgba(0, 102, 205, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "L3 Active",
        data: data.l3?.map((el) => (el ? Math.round(el) : el)),
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(205, 102, 0, 1)",
        backgroundColor: "rgba(205, 102, 0, 1)",
        pointStyle: false,
        type: "line",
      },
    ],
  };
  return <DeviceMeteringChart data={gridData} options={options} />;
};

const LiveReactiveChart = ({ device, labels, data }) => {
  const params = useParams();
  const backend = useBackend();

  let {
    data: siteData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  const options = getDeviceLiveChartOptions();
  options.scales.y.suggestedMin = -siteData?.config?.max_grid_inject_sp * 1.1;
  options.scales.y.suggestedMax = siteData?.config?.max_grid_offtake_sp * 1.1;

  options.scales.x = {
    type: "time",
    time: {
      unit: "minute",
      displayFormats: {
        minute: "HH:mm",
      },
    },
  };

  const gridData = {
    labels: labels,
    datasets: [
      {
        fill: false,
        label: "L1 Reactive",
        data: data.reactive_l1?.map((el) => (el ? Math.round(el) : el)),
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(102, 0, 205, 1)",
        backgroundColor: "rgba(102, 0, 205, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "L2 Rective",
        data: data.reactive_l2?.map((el) => (el ? Math.round(el) : el)),
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(0, 102, 205, 1)",
        backgroundColor: "rgba(0, 102, 205, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "L3 Reactive",
        data: data.reactive_l3?.map((el) => (el ? Math.round(el) : el)),
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(205, 102, 0, 1)",
        backgroundColor: "rgba(205, 102, 0, 1)",
        pointStyle: false,
        type: "line",
      },
    ],
  };
  return <DeviceMeteringChart data={gridData} options={options} />;
};

const LiveCurtailmentChart = ({ device, labels, data }) => {
  const options = getDeviceLiveChartOptions();
  options.scales.y.suggestedMin = 0;
  options.scales.y.suggestedMax = (device.inverter_power || device.charge_rate) * 1.1;

  const gridData = {
    labels: labels,
    datasets: [
      {
        fill: false,
        label: "Production",
        data: data.production?.map((value, index) => value - data.curtailment[index]),
        borderWidth: 2,
        //backgroundColor: 'transparent',
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(0, 204, 0, 1)",
        backgroundColor: "rgba(0, 204, 0, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "Production setpoint",
        data: data.production_setpoint?.map((el) => (el ? Math.round(el) : el)),
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(100, 100, 100, 0.5)",
        backgroundColor: "rgba(100, 100, 100, 0.5)",
        pointStyle: false,
        type: "line",
      },
    ],
  };
  return (
    <Box height="250px">
      <Line data={gridData} width={"100%"} options={options} />
    </Box>
  );
};

const DeviceReactiveChart = ({ labels, data }) => {
  const options = getDeviceLiveChartOptions();

  options.scales.x = {
    type: "time",
    time: {
      unit: "minute",
      displayFormats: {
        minute: "HH:mm",
      },
    },
  };

  const gridData = {
    labels: labels,
    datasets: [
      {
        fill: false,
        label: "L1 Reactive",
        data: data.reactive_l1,
        borderWidth: 2,
        //backgroundColor: 'transparent',
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(102, 0, 205, 1)",
        backgroundColor: "rgba(102, 0, 205, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "L2 Reactive",
        data: data.reactive_l2,
        borderWidth: 2,
        //backgroundColor: 'transparent',
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(0, 102, 205, 1)",
        backgroundColor: "rgba(0, 102, 205, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "L3 Reactive",
        data: data.reactive_l3,
        borderWidth: 2,
        //backgroundColor: 'transparent',
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(205, 102, 0, 1)",
        backgroundColor: "rgba(205, 102, 0, 1)",
        pointStyle: false,
        type: "line",
      },
    ],
  };
  return <DeviceMeteringChart data={gridData} options={options} />;
};
